import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPageStatusDisplay } from "abreact/misc/AbPageStatusDisplay";
import * as React from "react";
import { AbRouteLink } from "absdk/preliminary/commons/app/router/AbRouteLink";
import { NavigationService } from "abreact/incubator/ui/app/NavigationService";
import { noPropsConcreteRouteFactory } from "absdk/preliminary/commons/app/router/RoutingTypes";
export let KnownErrorType = /*#__PURE__*/function (KnownErrorType) {
  KnownErrorType[KnownErrorType["FORBIDDEN"] = 403] = "FORBIDDEN";
  KnownErrorType[KnownErrorType["PAGE_NOT_FOUND"] = 404] = "PAGE_NOT_FOUND";
  KnownErrorType[KnownErrorType["SERVICE_UNAVAILABLE"] = 503] = "SERVICE_UNAVAILABLE";
  return KnownErrorType;
}({});

/**
 * If user provides a KnownErrorInfo, then we render the default title, message, and body
 * for the type of known error provided. Users can override these default values with custom 
 * override values.
 */

/**
 * If user provides a CustomErrorInfo, then the user specifies all the status code, title,
 * message, and body. The status code provided is used to determine the icon that is rendered.
 */

const getDefaultPageStatePropsFromKnownErrorType = errorInfo => {
  let pageStateProp;
  if (errorInfo.knownErrorType === KnownErrorType.FORBIDDEN) {
    pageStateProp = {
      icon: "absdk:error-page-access-denied",
      title: AbReactResources.abHttpErrorPage_accessDenied_title(),
      message: AbReactResources.abHttpErrorPage_accessDenied_message()
    };
  } else if (errorInfo.knownErrorType === KnownErrorType.PAGE_NOT_FOUND) {
    pageStateProp = {
      icon: "absdk:error-page-404",
      title: AbReactResources.abHttpErrorPage_pageNotFound_title(),
      message: AbReactResources.abHttpErrorPage_pageNotFound_message(),
      body: /*#__PURE__*/React.createElement(AbRouteLink, {
        label: AbReactResources.abHttpErrorPage_pageNotFound_bodyLabel(),
        concreteRoute: noPropsConcreteRouteFactory("#", () => NavigationService.getSingleton().gotoHome()),
        tooltip: AbReactResources.abHttpErrorPage_pageNotFound_bodyLabel()
      }),
      className: "page-not-found-error" // we add this class to alter margin just for this image
    };
  } else if (errorInfo.knownErrorType === KnownErrorType.SERVICE_UNAVAILABLE) {
    pageStateProp = {
      icon: "absdk:error-page-503",
      title: AbReactResources.abHttpErrorPage_serviceUnavailable_title(),
      message: AbReactResources.abHttpErrorPage_serviceUnavailable_message()
    };
  } else {
    // we should never get here
    pageStateProp = {
      icon: "absdk:error-page-unknown",
      title: "",
      message: ""
    };
  }
  return {
    ...pageStateProp,
    icon: pageStateProp.icon,
    title: errorInfo.overrideTitle ?? pageStateProp.title,
    message: errorInfo.overrideMessage ?? pageStateProp.message,
    body: errorInfo.overrideBody ?? pageStateProp.body
  };
};
const getPageStateIconFromStatusCode = statusCode => {
  if (400 <= statusCode && statusCode < 500) {
    if (statusCode === 403) {
      return "absdk:error-page-access-denied";
    } else if (statusCode === 404) {
      return "absdk:error-page-404";
    }
    return "absdk:error-page-404"; // TODO: we need a separate icon for 4xx cases that is not 404
  } else if (statusCode >= 500) {
    return "absdk:error-page-503"; // TODO: do we want separate icon for 503?
  } else {
    return "absdk:error-page-unknown";
  }
};
const getPageStatePropsFromErrorInfo = errorInfo => {
  if ("statusCode" in errorInfo) {
    return {
      ...errorInfo,
      icon: getPageStateIconFromStatusCode(errorInfo.statusCode)
    };
  } else {
    return getDefaultPageStatePropsFromKnownErrorType(errorInfo);
  }
};
/**
 * AbHttpErrorPage displays a flushed error page for HTTP related errors. 
 * 
 * If CustomErrorInfo is provided, the page will feature an icon corresponding to the
 * specified status code, along with a user-defined title, message, and body. Status 
 * codes within the ranges 400 to 499 will share the same icon, as will those within 
 * the 500 and above range. All other status codes also will have a different but 
 * consistent icon.
 * 
 * If KnownErrorInfo is provided, the page will display the default icon, title, message, 
 * and body based on the error type, unless an override is specified.
 * 
 * For full-screen error pages that are not related to HTTP errors or if you need further
 * flexibility, consider using AbPageStatusDisplay. 
 */
export function AbHttpErrorPage(props) {
  const commonProps = ReactUtils.extractCommonProps(props);
  const pageStateProps = getPageStatePropsFromErrorInfo(props.errorInfo);
  return /*#__PURE__*/React.createElement(AbPageStatusDisplay, _extends({}, commonProps, pageStateProps, {
    className: ReactUtils.componentClassNamesString("ab-http-error-page", props, [pageStateProps.className])
  }));
}
//# sourceMappingURL=AbHttpErrorPage.js.map
